import {useLingui} from '@lingui/react/macro'

import {
  useAccountAssetsQuery,
  useAccountBillsQuery,
  useClaimDailyFreeMutation,
} from '#/state/queries/bills'
import {useSession} from '#/state/session'
import * as Toast from '#/components/Toast'

export interface BillsContextValue {
  bills: number
  coins: number
  deductBill: () => 'ok' | 'empty'
  addBills: (n: number) => void
  addCoins: (n: number) => void
  convertCoins: (amount: number) => boolean
  claimDailyFree: () => void
  showToast: (msg: string, type?: 'coin' | 'bill' | 'error' | 'success') => void
}

const COINS_TO_BILL = 100

export function useBills(): BillsContextValue {
  const {t: l} = useLingui()
  const {currentAccount} = useSession()
  const did = currentAccount?.did ?? ''

  const billsQuery = useAccountBillsQuery({did})
  const assetsQuery = useAccountAssetsQuery({did})
  const claimMutation = useClaimDailyFreeMutation()

  const bills = billsQuery.data?.remainingBills ?? 0
  const coins = assetsQuery.data?.coins ?? 0

  const showToast = (
    msg: string,
    type?: 'coin' | 'bill' | 'error' | 'success',
  ) => {
    const toastType =
      type === 'error' ? 'error' : type === 'success' ? 'success' : 'default'
    Toast.show(msg, {type: toastType})
  }

  const deductBill = (): 'ok' | 'empty' => {
    if (bills <= 0) return 'empty'
    // consumeBills needs capsule-specific params — called separately at capsule-open time.
    // This just guards the UI gate; the actual deduction happens via useConsumeBillsMutation.
    return 'ok'
  }

  const addBills = (_n: number) => {
    // Callers with full capsule context should use useAddAccountBillsMutation directly.
    // Refetch so the UI reflects any server-side change.
    void billsQuery.refetch()
  }

  const addCoins = (_n: number) => {
    // No standalone "add coins" API — refetch assets to pick up any server-side change.
    void assetsQuery.refetch()
  }

  const convertCoins = (amount: number): boolean => {
    // No coins-to-bills conversion API yet. Validate locally so UI can gate the action,
    // but return false — caller must not treat this as a confirmed server operation.
    if (amount < COINS_TO_BILL || coins < amount) return false
    void assetsQuery.refetch()
    void billsQuery.refetch()
    return false
  }

  const claimDailyFree = () => {
    if (!did) return
    claimMutation.mutate(
      {did},
      {
        onSuccess: result => {
          if (result.claimed) {
            showToast(l`+20 coins claimed!`, 'coin')
          }
        },
        onError: () => {
          showToast(l`Could not claim coins.`, 'error')
        },
      },
    )
  }

  return {
    bills,
    coins,
    deductBill,
    addBills,
    addCoins,
    convertCoins,
    claimDailyFree,
    showToast,
  }
}
